import "./index.css"

import { useId } from "@utilities/useId"
import classNames from "classnames"
import React, { forwardRef, InputHTMLAttributes } from "react"

export type RadioSize = "md" | "sm"

interface RadioBaseProps {
  /** Optional description text that appears below the radio label. */
  description?: string
  /** The name of the radio group. All radios in the group must have the same name. */
  name: string
  /** The size of the radio. */
  size?: RadioSize
}

interface RadioWithLabel extends RadioBaseProps {
  "aria-labelledby"?: never
  /**
   * Label content for the radio. When using non-string content
   * (e.g. JSX), it must not contain interactive elements. This
   * includes native HTML elements such as `<a>` and `<button>`, as
   * well as Tapestry or Tapestry-React interactive components.
   */
  label: React.ReactNode
}

interface RadioWithAriaLabelledBy extends RadioBaseProps {
  "aria-labelledby": string
  label?: never
}

export type RadioProps = RadioWithLabel | RadioWithAriaLabelledBy

export type RadioElementProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  keyof RadioProps | "type"
> &
  RadioProps

/**
 * A radio component that renders as a radio input with label.
 * Supports various sizes.
 * Must have a name attribute.
 *
 * **Required:** You must provide either:
 * - `label` - Label content for the radio
 * - `aria-labelledby` - ID of an external element that provides the label
 *
 * When using non-string content for `label` (e.g. JSX), it must not contain
 * interactive elements. This includes native HTML elements such as `<a>` and
 * `<button>`, as well as Tapestry or Tapestry-React interactive components.
 *
 * @component
 */
export const Radio = forwardRef<HTMLInputElement, RadioElementProps>(
  (
    {
      className,
      description,
      id,
      label,
      size = "md",
      ...restProps
    }: RadioElementProps,
    ref
  ) => {
    const combinedClassName = classNames(
      "tds-radio",
      size && size === "sm" && `tds-radio--sm`,
      className
    )

    const stableId = useId()
    const radioId = id || `tds-radio-${stableId}`

    return (
      <div className={combinedClassName}>
        <input
          {...restProps}
          aria-describedby={description ? `${radioId}-description` : undefined}
          id={radioId}
          ref={ref}
          type="radio"
        />
        {label && <label htmlFor={radioId}>{label}</label>}
        {description && (
          <p id={`${radioId}-description`} className="tds-radio-description">
            {description}
          </p>
        )}
      </div>
    )
  }
)

Radio.displayName = "Radio"
